/**
 * Change Password Modal Component
 * 
 * Multi-step modal for password change (logged-in users):
 * 1. Current password - Verify current password
 * 2. OTP verification - Enter the code sent to email
 * 3. New password - Set new password
 * 4. Success - Confirmation
 */

import { useState, useRef, useEffect } from "react"
import type { KeyboardEvent, ClipboardEvent } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { 
  Lock, 
  KeyRound, 
  ShieldCheck, 
  CheckCircle2, 
  Loader2, 
  ArrowLeft, 
  Eye, 
  EyeOff,
  AlertCircle
} from "lucide-react"
import { cn } from "@/shared/lib/utils"

// ============================================================
// TYPES
// ============================================================

type Step = 'current' | 'otp' | 'new' | 'success'

interface ChangePasswordModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

// ============================================================
// COMPONENT
// ============================================================

export function ChangePasswordModal({ 
  open, 
  onOpenChange, 
  onSuccess 
}: ChangePasswordModalProps) {
  // Step state
  const [step, setStep] = useState<Step>('current')
  
  // Form data
  const [currentPassword, setCurrentPassword] = useState('')
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''))
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  
  // UI state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  
  // OTP input refs
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])
  
  // Get session ID
  const getSessionId = () => sessionStorage.getItem('session_id') || ''
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep('current')
        setCurrentPassword('')
        setOtp(Array(6).fill(''))
        setNewPassword('')
        setConfirmPassword('')
        setError(null)
        setResendCooldown(0)
      }, 200)
    }
  }, [open])
  
  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown(prev => prev - 1)
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [resendCooldown])
  
  // Focus first OTP input when entering OTP step
  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100)
    }
  }, [step])
  
  // ============================================================
  // API CALLS
  // ============================================================
  
  const requestChangeOTP = async () => {
    // Validate current password first
    if (!currentPassword) {
      setError('Please enter your current password')
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      // Request OTP for password change
      const response = await fetch('/api/v2/password.php/send-change-otp', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-ID': getSessionId()
        },
        body: JSON.stringify({ forceNew: false })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setResendCooldown(60)
        setStep('otp')
      } else {
        setError(data.message || 'Failed to send verification code')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }
  
  const changePassword = async () => {
    const otpCode = otp.join('')
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code')
      return
    }
    
    // Validate new password
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long')
      return
    }
    
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('New password must contain at least one lowercase letter, one uppercase letter, and one number')
      return
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch('/api/v2/password.php/change', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-ID': getSessionId()
        },
        body: JSON.stringify({ 
          currentPassword,
          newPassword,
          otp: otpCode
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setStep('success')
        onSuccess?.()
      } else {
        setError(data.message || 'Failed to change password')
        // If OTP error, clear OTP
        if (data.message?.toLowerCase().includes('code') || data.message?.toLowerCase().includes('otp')) {
          setOtp(Array(6).fill(''))
          otpInputRefs.current[0]?.focus()
        }
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }
  
  const resendCode = async () => {
    if (resendCooldown > 0) return
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch('/api/v2/password.php/send-change-otp', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Session-ID': getSessionId()
        },
        body: JSON.stringify({ forceNew: true })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setResendCooldown(60)
        setOtp(Array(6).fill(''))
        otpInputRefs.current[0]?.focus()
      } else {
        setError(data.message || 'Failed to resend code')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }
  
  // ============================================================
  // OTP INPUT HANDLERS
  // ============================================================
  
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    
    const newOtp = [...otp]
    newOtp[index] = value.slice(-1)
    setOtp(newOtp)
    
    if (error) setError(null)
    
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }
  
  const handleOtpKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp]
        newOtp[index - 1] = ''
        setOtp(newOtp)
        otpInputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }
  
  const handleOtpPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData) {
      const newOtp = Array(6).fill('')
      for (let i = 0; i < pastedData.length; i++) {
        newOtp[i] = pastedData[i]
      }
      setOtp(newOtp)
      const lastIndex = Math.min(pastedData.length - 1, 5)
      otpInputRefs.current[lastIndex]?.focus()
    }
  }
  
  // ============================================================
  // RENDER HELPERS
  // ============================================================
  
  const getStepTitle = () => {
    switch (step) {
      case 'current': return 'Change Password'
      case 'otp': return 'Verify Your Identity'
      case 'new': return 'Create New Password'
      case 'success': return 'Password Changed'
    }
  }
  
  const getStepDescription = () => {
    switch (step) {
      case 'current': return 'Enter your current password to continue.'
      case 'otp': return 'We sent a verification code to your email.'
      case 'new': return 'Create a strong password for your account.'
      case 'success': return 'Your password has been changed successfully.'
    }
  }
  
  const getStepIcon = () => {
    switch (step) {
      case 'current': return <Lock className="w-6 h-6 text-orange-600" />
      case 'otp': return <KeyRound className="w-6 h-6 text-orange-600" />
      case 'new': return <ShieldCheck className="w-6 h-6 text-orange-600" />
      case 'success': return <CheckCircle2 className="w-6 h-6 text-green-600" />
    }
  }
  
  // ============================================================
  // RENDER
  // ============================================================
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <div className="mx-auto p-3 bg-orange-50 rounded-full">
            {getStepIcon()}
          </div>
          <DialogTitle className="text-center text-xl font-semibold">
            {getStepTitle()}
          </DialogTitle>
          <DialogDescription className="text-center text-gray-600">
            {getStepDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          {/* Step: Current Password */}
          {step === 'current' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Enter your current password"
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value)
                      if (error) setError(null)
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && requestChangeOTP()}
                    className="pr-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                onClick={requestChangeOTP}
                disabled={loading || !currentPassword}
                className="w-full btn-autumn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending Code...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          )}
          
          {/* Step: OTP */}
          {step === 'otp' && (
            <div className="space-y-4">
              {/* OTP Input */}
              <div className="flex justify-center gap-2">
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => { otpInputRefs.current[index] = el }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    className={cn(
                      "w-12 h-14 text-center text-xl font-semibold",
                      "border-2 border-gray-300 rounded-lg",
                      "focus:border-orange-500 focus:ring-orange-500",
                      digit && "border-orange-400 bg-orange-50"
                    )}
                  />
                ))}
              </div>
              
              {/* Resend Code */}
              <div className="text-center">
                {resendCooldown > 0 ? (
                  <p className="text-sm text-gray-500">
                    Resend code in <span className="font-semibold text-orange-600">{resendCooldown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={resendCode}
                    disabled={loading}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Didn't receive a code? Resend
                  </button>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('current')}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep('new')}
                  disabled={otp.join('').length !== 6}
                  className="flex-1 btn-autumn-primary"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
          
          {/* Step: New Password */}
          {step === 'new' && (
            <div className="space-y-4">
              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value)
                      if (error) setError(null)
                    }}
                    className="pr-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Min. 8 characters with uppercase, lowercase, and number
                </p>
              </div>
              
              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-new-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      if (error) setError(null)
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && changePassword()}
                    className="pr-10 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className={cn(
                  "flex items-center gap-2 text-sm",
                  newPassword === confirmPassword ? "text-green-600" : "text-red-500"
                )}>
                  {newPassword === confirmPassword ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Passwords match
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      Passwords do not match
                    </>
                  )}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setStep('otp')}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={changePassword}
                  disabled={loading || !newPassword || !confirmPassword}
                  className="flex-1 btn-autumn-primary"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Step: Success */}
          {step === 'success' && (
            <div className="space-y-4 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              
              <p className="text-gray-600">
                Your password has been changed. All other sessions have been logged out for security.
              </p>
              
              <Button
                onClick={() => onOpenChange(false)}
                className="w-full btn-autumn-primary"
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
